import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiBook, FiPieChart, FiBell, FiTarget, FiGrid, FiSettings, FiShield, FiFlag, FiLogOut, FiHelpCircle, FiUsers, FiMenu, FiX, FiCheck, FiCheckCircle, FiMoon, FiSun } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useTone } from '../contexts/ToneContext';
import { useTheme } from '../contexts/ThemeContext';
import { CurrencyIcon } from '../utils/currency';
import { notificationAPI } from '../services/api';

import BottomNavigation from './BottomNavigation';
import AddTransactionModal from './AddTransactionModal';
import OfflineIndicator from './OfflineIndicator';
import syncEngine from '../services/syncEngine';

import logoNavbar from '../assets/logo-navbar.png';
import './Layout.css';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useTone();
  const { isDark, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  // Dropdown states
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const notifDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationAPI.getUnreadCount();
      setUnreadCount(res.data?.data?.count || 0);
    } catch { /* non-critical */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll({ limit: 8 });
      setNotifications(res.data?.data?.notifications || res.data?.data || []);
    } catch { /* non-critical */ }
  }, []);

  // Fetch unread count on mount and when route changes
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchNotifications();
    }
  }, [user, location.pathname, fetchUnreadCount, fetchNotifications]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setShowNotifDropdown(false);
    setShowProfileDropdown(false);
  }, [location.pathname]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSideNavCollapsed, setIsSideNavCollapsed] = useState(false);

  const isAdmin = user?.role === 'app_admin' || user?.role === 'super_admin';
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const handleAddTransaction = () => setIsAddModalOpen(true);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch { /* non-critical */ }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* non-critical */ }
  };

  const toggleNotifDropdown = () => {
    setShowNotifDropdown(!showNotifDropdown);
    setShowProfileDropdown(false);
    if (!showNotifDropdown) fetchNotifications();
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    setShowNotifDropdown(false);
  };

  // Initialize offline sync engine
  useEffect(() => {
    syncEngine.init().catch((error) => {
      console.error('Failed to initialize sync engine:', error);
    });
  }, []);

  // Navigation items
  const navItems = [
    { path: '/dashboard', icon: FiHome, label: t('dashboard.title') },
    { path: '/ledgers', icon: FiBook, label: t('ledger.title') },
    { path: '/transactions', icon: CurrencyIcon, label: t('transactions.title') },
    { path: '/budgets', icon: FiTarget, label: t('budgets.title') },
    { path: '/vendors', icon: FiUsers, label: t('vendors.title') },
    { path: '/reports', icon: FiPieChart, label: t('reports.title') },
    { path: '/reminders', icon: FiBell, label: t('reminders.title') },
    { path: '/categories', icon: FiGrid, label: t('categories.title') },
  ];

  const roleLabel = user?.role === 'super_admin' ? 'Super Admin'
    : user?.role === 'app_admin' ? 'Admin'
    : 'User';

  return (
    <div className="app-layout">
      {/* Fixed Side Navigation - Desktop Only */}
      <aside className={`side-nav ${isSideNavCollapsed ? 'collapsed' : ''}`}>
        <div className="side-nav-header">
          <Link to="/dashboard" className="side-nav-logo">
            <img src={logoNavbar} alt="Hisab OK Hai" className="side-nav-logo-img" />
            {!isSideNavCollapsed && (
              <div className="side-nav-logo-text">
                <span className="side-nav-brand">Hisab OK Hai</span>
                <span className="side-nav-tagline">Roz ka kharcha, bina tension</span>
              </div>
            )}
          </Link>
          <button
            className="side-nav-toggle"
            onClick={() => setIsSideNavCollapsed(!isSideNavCollapsed)}
            title={isSideNavCollapsed ? 'Expand' : 'Collapse'}
          >
            <FiMenu />
          </button>
        </div>

        <nav className="side-nav-menu">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`side-nav-link ${isActive(item.path) ? 'active' : ''}`}
                title={isSideNavCollapsed ? item.label : ''}
              >
                <Icon className="side-nav-icon" />
                {!isSideNavCollapsed && <span className="side-nav-label">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="side-nav-footer">
          <Link
            to="/settings"
            className={`side-nav-link ${isActive('/settings') ? 'active' : ''}`}
            title={isSideNavCollapsed ? t('settings.title') : ''}
          >
            <FiSettings className="side-nav-icon" />
            {!isSideNavCollapsed && <span className="side-nav-label">{t('settings.title')}</span>}
          </Link>
          <Link
            to="/help"
            className={`side-nav-link ${isActive('/help') ? 'active' : ''}`}
            title={isSideNavCollapsed ? 'Help Center' : ''}
          >
            <FiHelpCircle className="side-nav-icon" />
            {!isSideNavCollapsed && <span className="side-nav-label">Help Center</span>}
          </Link>
          {isAdmin && (
            <Link
              to="/app-admin"
              className={`side-nav-link ${isActive('/app-admin') ? 'active' : ''}`}
              title={isSideNavCollapsed ? 'Admin' : ''}
            >
              <FiShield className="side-nav-icon" />
              {!isSideNavCollapsed && <span className="side-nav-label">Admin</span>}
            </Link>
          )}
        </div>
      </aside>

      {/* Main Container */}
      <div className="main-container">
        {/* Top Bar */}
        <header className="top-bar">
          <div className="top-bar-left">
            <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
              <FiMenu />
            </button>
            <Link to="/dashboard" className="mobile-logo">
              <img src={logoNavbar} alt="Hisab OK Hai" className="mobile-logo-img" />
              <span className="mobile-logo-text">Hisab OK Hai</span>
            </Link>
          </div>

          <div className="top-bar-right">
            {/* Notification Bell with Dropdown */}
            <div className="dropdown-wrapper" ref={notifDropdownRef}>
              <button
                className="top-bar-icon-btn notification-bell"
                onClick={toggleNotifDropdown}
                title={t('reminders.title')}
              >
                <FiBell />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
              {showNotifDropdown && (
                <div className="dropdown-menu dropdown-notifications">
                  <div className="dropdown-header">
                    <span className="dropdown-header-title">Notifications</span>
                    {unreadCount > 0 && (
                      <button className="dropdown-header-action" onClick={handleMarkAllAsRead}>
                        <FiCheckCircle size={14} />
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="dropdown-body">
                    {notifications.length === 0 ? (
                      <div className="dropdown-empty">No notifications</div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`dropdown-notif-item ${!n.is_read ? 'unread' : ''}`}
                        >
                          <div className="dropdown-notif-content">
                            <p className="dropdown-notif-title">{n.title}</p>
                            <p className="dropdown-notif-msg">{n.message}</p>
                            <span className="dropdown-notif-time">
                              {new Date(n.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          {!n.is_read && (
                            <button
                              className="dropdown-notif-read-btn"
                              onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n.id); }}
                              title="Mark as read"
                            >
                              <FiCheck size={14} />
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="dropdown-footer">
                    <Link
                      to="/reminders"
                      className="dropdown-footer-link"
                      onClick={() => setShowNotifDropdown(false)}
                    >
                      View all
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Dropdown — single wrapper for both mobile + desktop */}
            <div className="dropdown-wrapper dropdown-wrapper-profile" ref={profileDropdownRef}>
              {/* Desktop trigger (hidden on mobile via .top-bar-user) */}
              <div className="top-bar-user">
                <button className="user-profile-btn" onClick={toggleProfileDropdown}>
                  <span className="user-avatar">{user?.full_name?.charAt(0) || 'U'}</span>
                  <span className="user-name-display">{user?.full_name || 'User'}</span>
                </button>
              </div>
              {/* Mobile trigger (hidden on desktop via .mobile-profile-btn) */}
              <button className="mobile-profile-btn" onClick={toggleProfileDropdown}>
                {user?.full_name?.charAt(0) || 'U'}
              </button>
              {showProfileDropdown && (
                <div className="dropdown-menu dropdown-profile">
                  <div className="dropdown-profile-header">
                    <span className="dropdown-profile-avatar">{user?.full_name?.charAt(0) || 'U'}</span>
                    <div className="dropdown-profile-info">
                      <p className="dropdown-profile-name">{user?.full_name || 'User'}</p>
                      <p className="dropdown-profile-email">{user?.email}</p>
                      <span className="dropdown-profile-role">{roleLabel}</span>
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <div className="dropdown-body">
                    <button className="dropdown-item" onClick={() => { toggleDarkMode(); }}>
                      {isDark ? <FiSun size={16} /> : <FiMoon size={16} />}
                      <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                    <Link to="/settings" className="dropdown-item" onClick={() => setShowProfileDropdown(false)}>
                      <FiSettings size={16} />
                      <span>Settings</span>
                    </Link>
                  </div>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
                    <FiLogOut size={16} />
                    <span>{t('action.logout')}</span>
                  </button>
                </div>
              )}
            </div>

            <button onClick={handleLogout} className="top-bar-logout-btn" title={t('action.logout')}>
              <FiLogOut />
              <span className="logout-text">{t('action.logout')}</span>
            </button>
          </div>
        </header>

        {/* Mobile Sidebar Overlay */}
        <div className={`mobile-sidebar-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}>
          <div className="mobile-sidebar-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sidebar-header">
              <div className="mobile-user-profile">
                <span className="mobile-user-avatar">{user?.full_name?.charAt(0) || 'U'}</span>
                <div className="mobile-user-info">
                  <p className="mobile-user-name">{user?.full_name || 'User'}</p>
                  <p className="mobile-user-email">{user?.email}</p>
                </div>
              </div>
              <button className="mobile-close-btn" onClick={closeMobileMenu}>
                <FiX />
              </button>
            </div>

            <nav className="mobile-nav-menu">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    <Icon className="mobile-nav-icon" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              <div className="mobile-nav-divider" />

              <Link to="/settings" className={`mobile-nav-link ${isActive('/settings') ? 'active' : ''}`} onClick={closeMobileMenu}>
                <FiSettings className="mobile-nav-icon" />
                <span>{t('settings.title')}</span>
              </Link>
              <Link to="/help" className={`mobile-nav-link ${isActive('/help') ? 'active' : ''}`} onClick={closeMobileMenu}>
                <FiHelpCircle className="mobile-nav-icon" />
                <span>Help Center</span>
              </Link>
              <Link to="/progress" className={`mobile-nav-link ${isActive('/progress') ? 'active' : ''}`} onClick={closeMobileMenu}>
                <FiFlag className="mobile-nav-icon" />
                <span>Roadmap</span>
              </Link>
              {isAdmin && (
                <Link to="/app-admin" className={`mobile-nav-link ${isActive('/app-admin') ? 'active' : ''}`} onClick={closeMobileMenu}>
                  <FiShield className="mobile-nav-icon" />
                  <span>Admin Dashboard</span>
                </Link>
              )}

              <button onClick={handleLogout} className="mobile-logout-btn">
                <FiLogOut className="mobile-nav-icon" />
                <span>{t('action.logout')}</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="content-area">
          <div className="content-wrapper">{children}</div>
        </main>

        {/* Footer */}
        <footer className="app-footer">
          <div className="footer-content">
            <div className="footer-links">
              <Link to="/help" className="footer-link">
                <FiHelpCircle size={14} style={{ marginRight: 4 }} />
                Help
              </Link>
              <Link to="/progress" className="footer-link">Roadmap</Link>
            </div>
            <div className="footer-copyright">
              <p>&copy; 2026 {process.env.REACT_APP_BUSINESS_NAME || 'INDIATECH247'}. All rights reserved.</p>
            </div>
            <div className="footer-contact">
              <p>Contact: {process.env.REACT_APP_BUSINESS_PHONE || '99115 20247'}</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNavigation onAddTransaction={handleAddTransaction} />

      {/* Add Transaction Modal */}
      <AddTransactionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {/* Offline Status */}
      <OfflineIndicator />
    </div>
  );
};

export default Layout;
